import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../helper/api';
import FinanceAccountSelector from './FinanceAccountSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import CategorySelector from './CategorySelector';

const ExpenseForm = ({ token }) => {
    const [form, setForm] = useState({
        user_id: 4,
        name: '',
        date: new Date().toISOString().slice(0, 10),
        amount: '',
        description: '',
        payment_method: 'cash',
        finance_account_id: '',
        finance_category_id: '',
        status: 'approved'
    });

    const [accounts, setAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const [accountsRes, categoriesRes] = await Promise.all([
                axios.get(`${API_URL}/finance-accounts?token=${token}`),
                axios.get(`${API_URL}/finance-categories?token=${token}`)
            ]);

            setAccounts(accountsRes.data.data);
            const debitCategories = categoriesRes.data.data.filter(c => c.type === 'debit');
            setCategories(debitCategories);
        } catch (error) {
            console.error('Failed to fetch initial data:', error);
            alert('Gagal memuat data awal');
        } finally {
            setIsLoadingData(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        if (type === 'file') {
            setForm({ ...form, [name]: e.target.files[0] });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleAccountSelect = (accountId) => {
        setForm({ ...form, finance_account_id: accountId });
    };

    const handleCategorySelect = (categoryId) => {
        setForm({ ...form, finance_category_id: categoryId });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.finance_account_id) {
            alert('Pilih akun pembayaran terlebih dahulu');
            return;
        }

        if (!form.finance_category_id) {
            alert('Pilih kategori pengeluaran terlebih dahulu');
            return;
        }

        if (!form.name || !form.amount) {
            alert('Lengkapi nama dan jumlah pengeluaran');
            return;
        }

        setIsLoading(true);

        try {
            await axios.post(`${API_URL}/expenses?token=${token}`, form);
            alert('✅ Berhasil input pengeluaran!');
            resetForm();
        } catch (error) {
            console.error('Expense submission error:', error);
            alert('❌ Gagal input pengeluaran');
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setForm({
            user_id: 4,
            name: '',
            date: new Date().toISOString().slice(0, 10),
            amount: '',
            description: '',
            payment_method: 'cash',
            finance_account_id: '',
            finance_category_id: '',
            status: 'approved'
        });
    };

    if (isLoadingData) {
        return (
            <div className="w-full max-w-md mt-5">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <div className="text-center">
                        <p className="text-gray-600">Memuat data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md mt-5">
            <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-3xl shadow-xl border border-white/20 p-8 relative overflow-hidden">
                {/* Animated background elements */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-indigo-400/20 to-purple-400/20 rounded-full blur-2xl animate-pulse"></div>
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-br from-pink-400/20 to-rose-400/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>

                {/* Header */}
                <div className="text-center mb-8 relative z-10">
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                        Input Pengeluaran
                    </h1>
                    <p className="text-gray-600 text-sm">Catat pengeluaran katsikat dengan mudah</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                    {/* Finance Account Selector */}
                    <FinanceAccountSelector
                        accounts={accounts}
                        selectedAccountId={form.finance_account_id}
                        onAccountSelect={handleAccountSelect}
                    />

                    {/* Basic Form Fields */}
                    <div className="space-y-4">
                        <div className="group">
                            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors">
                                Nama Pengeluaran
                            </label>
                            <div className="relative">
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Contoh: Beli deterjen"
                                    required
                                    className="w-full px-4 py-4 bg-gray-50/50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-indigo-500 focus:bg-white transition-all duration-300 placeholder-gray-400 hover:border-gray-300 hover:bg-gray-50"
                                />
                            </div>
                        </div>

                        <div className="group">
                            <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors">
                                Tanggal
                            </label>
                            <div className="relative">
                                <input
                                    id="date"
                                    name="date"
                                    type="date"
                                    value={form.date}
                                    onChange={handleChange}
                                    className="w-full px-4 py-4 bg-gray-50/50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-indigo-500 focus:bg-white transition-all duration-300 hover:border-gray-300 hover:bg-gray-50"
                                />
                            </div>
                        </div>

                        <div className="group">
                            <label htmlFor="amount" className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors">
                                Jumlah (Rp)
                            </label>
                            <div className="relative">
                                <input
                                    id="amount"
                                    name="amount"
                                    type="number"
                                    value={form.amount}
                                    onChange={handleChange}
                                    placeholder="Contoh: 50000"
                                    required
                                    min="1"
                                    className="w-full px-4 py-4 bg-gray-50/50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-indigo-500 focus:bg-white transition-all duration-300 placeholder-gray-400 hover:border-gray-300 hover:bg-gray-50"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                Keterangan (Opsional)
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                placeholder="Contoh: Beli sabun, ke Indomaret"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            />
                        </div>
                    </div>

                    {/* Payment Method Selector */}
                    <PaymentMethodSelector
                        selectedMethod={form.payment_method}
                        onMethodChange={handleChange}
                    />

                    {/* Category Selector */}
                    <CategorySelector
                        categories={categories}
                        selectedCategoryId={form.finance_category_id}
                        onCategorySelect={handleCategorySelect}
                    />

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
                    >
                        {isLoading ? 'Memproses...' : 'Kirim Pengeluaran'}
                    </button>
                </form>

                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-indigo-400 rounded-full animate-ping"></div>
                <div className="absolute bottom-4 left-4 w-1 h-1 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
            </div>
        </div>
    );
};

export default ExpenseForm; 